import { Collapse, Tab, Tabs } from "@blueprintjs/core";
import React from "react";
import { addEnum } from "typegraph";
import draftToHtml from "draftjs-to-html";
import Lifecycle from "common/entities/Lifecycle";
import { ILayout } from "common/form/fields/layout/ILayoutType";
import DropdownSelect from "common/form/widgets/common/DropdownSelect";
import { FlexJustify } from "common/fragments/Flex";
import HtmlDefault from "common/fragments/HtmlDefault";
import Loading from "common/fragments/Loading";
import NonIdealState from "common/fragments/NonIdealState";
import { ButtonView } from "common/views/button/Button.view";
import { CardView } from "common/views/card/Card.view";
import HeadingView from "common/views/heading/Heading.view";
import { ImageView } from "common/views/image/Image.view";
import TypographyView from "common/views/typographyView/Typography.view";
import AutoForm from "core/components/AutoForm";
import { EntityEditWrapper } from "core/components/EntityEditWrapper";
import {
	FragmentLayout,
	IFragmentLayoutConfig
} from "core/components/FragmentLayout";
import Label from "core/components/label/Label";
import AppToaster from "core/util/AppToaster";
import TokenSession from "core/util/session/TokenSession";
import * as articleFragments from "../article/fragments";
import { ArticleContributorList } from "../articleContributor/admin/ArticleContributorList";
import { ArticleContributor } from "../articleContributor/entities";
import AttachmentList from "../attachment/AttachmentList";
import { Article } from "./entities";
import { ArticleTypeRegistry } from "./IArticleType";

import * as articleActions from "./arcticleActions";
import convertToHtml from "common/fragments/ConvertToHtml";
import ArticleGlossaryTermListSearch from "./ArticleGlossaryTermListSearch";

const ArticleEditWrapper = EntityEditWrapper.ofType<Article>();

interface IArticleEditProps {
	articleId?: string | number;
	articleGuid?: string;
	articleExternalId?: string;
	categoryRefId?: string | number;
	layout?: ILayout<IFragmentLayoutConfig>;
	actions?: typeof articleActions;
	knowledgeBaseId?: string | number;
}

export class ArticleEdit extends React.Component<IArticleEditProps> {
	public render() {
		const params = {};

		if (this.props.articleId) {
			params["id"] = this.props.articleId;
		} else if (this.props.articleGuid) {
			params["guid"] = this.props.articleGuid;
		} else if (this.props.articleExternalId) {
			params["externalId"] = this.props.articleExternalId;
		} else {
			return <NonIdealState title="No Article Selected" description="" />;
		}

		// check roles on back end to determine
		// if this should be skipped
		params["trackRequest"] = true;

		if (TokenSession.hasRoles("ROLE_ADMIN", "ROLE_AUTHOR")) {
			// if user has authoring permissions, show all statuses
			params["statuses"] = [
				addEnum(Lifecycle.Available),
				addEnum(Lifecycle.Predeployed),
				addEnum(Lifecycle.Published),
				addEnum(Lifecycle.UnderConstruction),
				addEnum(Lifecycle.Deleted)
			];
		}

		return (
			<ArticleEditWrapper
				canEdit={TokenSession.hasRoles("ROLE_ADMIN", "ROLE_AUTHOR")}
				entity={Article}
				params={params}
				onAfterLoad={this.onAfterLoad}
				onBeforeSave={this.onBeforeSave}
				onAfterSave={this.onAfterSave}
				title={<Label k="article.edit" v="Edit Article" />}
			>
				{args => {
					if (args.loading) {
						return <Loading />;
					}

					if (!args.data) {
						return (
							<NonIdealState
								title="Article Not Found"
								description=""
							/>
						);
					}

					const articleType =
						(args.data.uiSchema &&
							ArticleTypeRegistry.getObject(
								args.data.uiSchema.type
							)) ||
						null;

					return (
						<div>
							<Collapse isOpen={args.editing}>
								<div
									style={{
										padding: "15px",
										backgroundColor: "#f5f8fa"
									}}
								>
									<Tabs
										id="article:edit"
										renderActiveTabPanelOnly={true}
									>
										<Tab
											id="general"
											title="General"
											panel={
												<AutoForm
													clazz={Article}
													value={args.data}
													onChange={article =>
														args.onChange({
															...args.data,
															...article
														})
													}
												/>
											}
										/>

										<Tab
											id="appearance"
											title="Appearance"
											panel={
												<div>
													<DropdownSelect
														options={ArticleTypeRegistry.getAll().map(
															at => ({
																value: at.type,
																label: at.label
															})
														)}
														value={
															args.data.uiSchema
																.type
														}
														onChange={type =>
															args.onChange({
																...args.data,
																uiSchema: {
																	...args.data
																		.uiSchema,
																	type
																}
															})
														}
													/>
													{articleType &&
														articleType.configClass && (
															<AutoForm
																clazz={
																	articleType.configClass
																}
																onChange={uiData =>
																	args.onChange(
																		{
																			...args.data,
																			uiSchema: {
																				...(args
																					.data
																					.uiSchema ||
																					({} as any)),
																				data: uiData
																			}
																		}
																	)
																}
																value={
																	args.data
																		.uiSchema
																		.data
																}
															/>
														)}
												</div>
											}
										/>
										<Tab
											id="attachments"
											title="Attachments"
											panel={
												<AttachmentList
													articleId={args.data.id}
												/>
											}
										/>
										<Tab
											id="contributors"
											title="Contributors"
											panel={
												<ArticleContributorList
													articleId={args.data.id}
												/>
											}
										/>
										<Tab
											id="analytics"
											title="Analytics"
											panel={
												<div>
													{`${(args.data.stat &&
														args.data.stat.views) ||
														0} Views`}
													More Info Coming Soon
												</div>
											}
										/>
										<Tab
											id="glossary"
											title="Glossary"
											panel={
												<ArticleGlossaryTermListSearch
													knowledgeBaseId={
														this.props
															.knowledgeBaseId
													}
													articleId={args.data.id}
													canCreate={TokenSession.hasRoles(
														"ROLE_ADMIN"
													)}
													canDelete={TokenSession.hasRoles(
														"ROLE_ADMIN"
													)}
													canUpdate={TokenSession.hasRoles(
														"ROLE_ADMIN"
													)}
													showPaginationOptions={TokenSession.hasRoles(
														"ROLE_ADMIN"
													)}
												/>
											}
										/>
									</Tabs>
								</div>
							</Collapse>
							<div>
								<FragmentLayout
									fragments={articleFragments}
									layout={this.props.layout}
									props={{
										article: args.data,
										actions: articleActions,
										categoryRefId: this.props.categoryRefId,
										uiSchema: args.data.uiSchema.data || {},
										contributorDisplay: this.renderContributors(
											args.data.contributors
										),
										attachmentDisplay: this.renderAttachments(
											args.data
										)
									}}
								/>
							</div>
						</div>
					);
				}}
			</ArticleEditWrapper>
		);
	}

	private onAfterLoad = (article: Article) => {
		return {
			...article,
			uiSchema:
				(article.uiSchema && JSON.parse(article.uiSchema as any)) || {},
			tempIconUrl: article.iconUrl
		};
	};

	private onBeforeSave = (article: Article) => {
		const updateParams = {
			id: article.id,
			name: article.name,
			displayName: article.displayName || null,
			keywords: article.keywords || null,
			description: convertToHtml(article.description) || null
		};

		if (!!article.tempIconUrl && article.iconUrl !== article.tempIconUrl) {
			updateParams["iconUrl"] = article.tempIconUrl;
		}

		if (article.uiSchema) {
			updateParams["uiSchema"] = JSON.stringify(article.uiSchema);
		}

		if (article.categories) {
			updateParams["categoryIds"] = article.categories.map(c => c.id);
		}

		if (article.tags) {
			updateParams["tagIds"] = article.tags.map(t => t.id);
		}

		return updateParams;
	};

	private onAfterSave = (res: any) => {
		AppToaster.show({
			message: "Article Saved",
			intent: "success"
		});
	};

	private renderContributors = (contributors: ArticleContributor[]) => {
		if (!contributors || contributors.length < 1) {
			return null;
		}

		return (
			<div>
				{contributors.map(ac => {
					return (
						<FlexJustify
							key={ac.id}
							style={{ alignItems: "flex-start" }}
						>
							{ac.contributor.imageURL && (
								<div style={{ paddingRight: "15px" }}>
									<ImageView
										viewKey="contributor.avatar"
										src={ac.contributor.imageURL}
									/>
								</div>
							)}
							<div style={{ flexGrow: 1 }}>
								<HeadingView viewKey="contributor.name">
									{ac.contributor.name}
								</HeadingView>
								{ac.description && (
									<TypographyView viewKey="contribution.description">
										<HtmlDefault html={ac.description} />
									</TypographyView>
								)}
								{ac.contributor.description && (
									<TypographyView viewKey="contributor.description">
										<HtmlDefault
											html={ac.contributor.description}
										/>
									</TypographyView>
								)}
							</div>
						</FlexJustify>
					);
				})}
			</div>
		);
	};

	private renderAttachments = (article: Article) => {
		if (!article.attachments || article.attachments.length < 1) {
			return null;
		}

		return (
			<div>
				{article.attachments.map(attachment => {
					return (
						<CardView
							viewKey="attachment"
							key={attachment.id}
							style={{ padding: "15px" }}
						>
							<FlexJustify>
								<div>
									<HeadingView viewKey="attachment.name">
										{attachment.file.displayName}
									</HeadingView>
									<TypographyView
										viewKey="attachment.description"
										html={attachment.file.description}
									/>
								</div>
								<div>
									<ButtonView
										viewKey="attachment.download"
										onClick={() =>
											window.open(
												attachment.fileUrl,
												"_blank"
											)
										}
									>
										<Label
											k="attachment.download"
											v="Download"
										/>
									</ButtonView>
								</div>
							</FlexJustify>
						</CardView>
					);
				})}
			</div>
		);
	};
}



// WEBPACK FOOTER //
// ./src/panels/kb/article/ArticleEdit.tsx